'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { ArrowRight, Fullscreen } from 'lucide-react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import React, { useState, useEffect } from 'react';
import Link from "next/link";
import { useSwipeable } from 'react-swipeable';

const reviews = [
  {
    name: "Sachin Bajpaee",
    role: "Founder, Amrtamya",
    text: "ExponentSolutions impressed us with innovative solutions, clear communication, and a strong grasp of our needs. The team is working on developing a unified, AI-powered system that integrates our existing applications and streamlines operations with detailed analytics for smarter decision-making.",
    image: "/Amrtamya.jpg"
  },
  {
    name: "Deepankar Joshi",
    role: "Co-founder, GTS",
    text: "They don’t just build software—they create intelligent ecosystems. Their AI-driven approach has brought clarity to our data and equipped us with predictive insights that support our daily decision-making.",
    image: "/GTS.jpg"
  },
  // {
  //   name: "Jinnesh Bohra",
  //   role: "Founder, Mahaveer Jewellers",
  //   text: "This tech team transformed our jewelry business with a custom web application tailored to our needs. From inventory and sales management to QR-based product tagging, multi-store operations, and payroll, everything is streamlined. Their dedication and expertise exceeded our expectations.",
  //   image: "/mahavir.jpg"
  // },
  {
    name: "Suneel Dhar",
    role: "Co-founder, skyjumpertrampolinepark",
    text: "We’re currently developing an AI-driven chatbot for Sky Jumper to automate bookings, handle FAQs, and enhance customer engagement. Though not live yet, there is part of a broader collaboration to build a complete end-to-end digital solution aimed at streamlining operations and scaling their business. A promising partnership in progress!",
    image: "/Sky-jumper.png"
  }
];

const ReviewCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + reviews.length) % reviews.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Add swipe support for mobile
  const handlers = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => prevSlide(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true // Optional: enable for desktop testing
  });

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-8 mt-8 sm:mt-16" role="region" aria-label="Testimonial Carousel">
      <div className="overflow-hidden relative">
        {/* Backdrop Blur Element (reduced intensity for mobile) */}
        <div className="absolute inset-x-4 sm:inset-x-32 bottom-0 h-[50%] w-[calc(100%-2rem)] sm:w-[60%] bg-blue-300 bg-opacity-5 backdrop-blur-sm sm:backdrop-blur-lg rounded-lg -z-10 mx-auto p-4 sm:p-6" />

        {/* Carousel Wrapper */}
        <div
          className="flex transition-transform duration-500 ease-in-out relative z-10"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          {...handlers}
        >
          {reviews.map((review, index) => (
            <div key={index} className="flex-shrink-0 w-full flex justify-center">
              <div className="bg-[#111111] text-white p-6 sm:p-14 rounded-xl shadow-lg max-w-md sm:max-w-lg relative">
                <div className="flex items-center gap-4 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden bg-gray-600">
                    <Image
                      src={review.image}
                      alt={review.name}
                      width={48}
                      height={48}
                      sizes="(max-width: 640px) 40px, 48px"
                      className="object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold">{review.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-400">{review.role}</p>
                  </div>
                  <div className="ml-auto text-gray-500 text-2xl sm:text-4xl leading-none">’’</div>
                </div>
                <p className="text-xs sm:text-sm text-gray-300">{review.text}</p>
                <div className="absolute inset-x-0 bottom-0 h-2 rounded-b-xl bg-gradient-to-r from-blue-600 to-purple-600 opacity-30 blur-sm"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 text-white p-3 sm:p-2 hover:bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Previous testimonial"
      >
        <FaChevronLeft size={20} className="sm:w-6 sm:h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-0 top-1/2 transform -translate-y-1/2 text-white p-3 sm:p-2 hover:bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Next testimonial"
      >
        <FaChevronRight size={20} className="sm:w-6 sm:h-6" />
      </button>
    </div>
  );
};

export default function CombinedSection() {
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <motion.section
      className="bg-gradient-to-r from-[#00063A] via-[#425fae] to-[#00063A] py-8 sm:py-12 px-4 sm:px-8 relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={sectionVariants}
    >
      <Image
        src="/Group.png"
        alt="Background tech pattern"
        fill
        sizes="100vw"
        className="object-cover bg-transparent opacity-50"
        priority={false}
        loading="lazy"
      />

      <div className="relative z-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-white mb-6 sm:mb-8">What Our Client Says</h2>
        <ReviewCarousel />
      </div>

      <div className="relative z-20 mt-8 sm:mt-16 text-center">
        <h2 className="text-2xl sm:text-4xl font-bold mb-6 sm:mb-8 text-white drop-shadow-md">
          Ready to streamline your business? 
        </h2>

        <div className="relative inline-block">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-orange-500/30 to-transparent"
            initial={{ opacity: 0.3 }}
            whileHover={{ opacity: 0.6 }}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            className="absolute inset-0 rounded-full"
            initial={{ boxShadow: '0 0 10px 2px rgba(255, 165, 0, 0.3)' }}
            whileHover={{ boxShadow: '0 0 20px 8px rgba(255, 165, 0, 0.5)' }}
            transition={{ duration: 0.3 }}
          />
          <Link href="/contact">
            <button className="relative flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 bg-transparent text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group text-sm sm:text-base">
              <span className="relative z-10">Get in Touch <ArrowRight className="inline-block ml-2 w-4 h-4 sm:w-5 sm:h-5" /></span>
              <div className="absolute inset-0 bg-gradient-to-t from-transparent to-blue-900 opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </Link>
        </div>
      </div>
    </motion.section>
  );
}
